import React, { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import customerService from '@/services/customerService';
import authService from '@/services/authService';

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: {
    id: number;
    name: string;
    phone: string;
    is_app_user: boolean;
  } | null;
  onSuccess: () => void;
}

export const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  onClose,
  customer,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer) {
      setName(customer.name);
      setPhone(customer.phone);
    }
  }, [customer]);

  if (!customer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error('Nama pelanggan wajib diisi');
      return;
    }
    if (!phone.trim()) {
      showToast.error('Nomor HP pelanggan wajib diisi');
      return;
    }

    const arenaSlug = authService.getSlug() || localStorage.getItem('arenaSlug') || '';
    if (!arenaSlug) {
      showToast.error('Arena tidak ditemukan');
      return;
    }

    try {
      setSaving(true);
      await customerService.updateCustomer(arenaSlug, customer.id, {
        name: name.trim(),
        phone: phone.trim(),
      });
      showToast.success('Data pelanggan berhasil diperbarui');
      onSuccess();
      onClose();
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal memperbarui data pelanggan'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Data Pelanggan"
      subtitle={
        customer.is_app_user
          ? 'Pelanggan terdaftar dari aplikasi Sporto'
          : 'Perbarui informasi kontak pelanggan Walk-in'
      }
      size="md"
    >
      {customer.is_app_user ? (
        <div className="space-y-4 py-2">
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm leading-relaxed">
            Nama dan nomor HP pelanggan ini ditautkan dengan akun aplikasi Sporto dan tidak dapat
            diubah oleh mitra arena.
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-gray-500 text-xs font-medium">Nama Pelanggan</label>
              <p className="font-semibold text-gray-900">{customer.name}</p>
            </div>
            <div>
              <label className="text-gray-500 text-xs font-medium">Nomor HP</label>
              <p className="font-semibold text-gray-900">{customer.phone}</p>
            </div>
          </div>
          <div className="pt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm transition-colors cursor-pointer"
            >
              Tutup
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Nama Pelanggan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Hendra Wijaya"
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] text-sm text-gray-900 outline-none transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Nomor HP <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Contoh: 081234567890"
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] text-sm text-gray-900 outline-none transition-all font-mono"
              required
            />
          </div>

          <div className="pt-4 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-[#FC480D] hover:bg-[#E8490F] text-white font-semibold text-sm transition-colors shadow-xs disabled:opacity-50 cursor-pointer"
            >
              {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default EditCustomerModal;
